const BASE_URL = '/api';
const TOKEN_KEY = 'pf_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function clearTokenAndRedirect(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('pf_user');
  window.location.href = '/login';
}

function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly body: unknown;
  constructor(message: string, statusCode: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.body = body;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    clearTokenAndRedirect();
    throw new Error('Unauthorized');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}: ${text}`, response.status, text);
    }
    return undefined as T;
  }

  if (!response.ok) {
    const err = json as { message?: string; error?: string };
    throw new ApiError(err.message || err.error || `HTTP ${response.status}`, response.status, json);
  }

  return json as T;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<T> {
  const response = await fetch(buildUrl(path, params), {
    method: 'GET',
    headers: buildHeaders(),
  });
  return handleResponse<T>(response);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'PUT',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'PATCH',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(response);
}

export async function apiDelete(path: string): Promise<void> {
  const response = await fetch(buildUrl(path), {
    method: 'DELETE',
    headers: buildHeaders(),
  });
  return handleResponse<void>(response);
}

export async function apiDownload(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
  filename?: string,
): Promise<void> {
  const response = await fetch(buildUrl(path, params), {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (response.status === 401) {
    clearTokenAndRedirect();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Download failed: ${text}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? 'export';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers,
    body: formData,
  });
  return handleResponse<T>(response);
}

export { TOKEN_KEY };
